import React, { useEffect, useState } from 'react'
import { View, Text, StyleSheet, Animated, Platform } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import theme from '../../../theme'

/**
 * WebInitialLoader - Loader completo de pantalla con progreso
 * Muestra durante la carga inicial de la app hasta que:
 * - Footer app esté cargado
 * - Posts iniciales estén listos
 * - Layout esté estabilizado
 */
const WebInitialLoader = ({ 
    progress = 0,  // 0-100
    message = 'Cargando...',
    subMessage = null,
    visible = true 
}) => {
    const fadeAnim = React.useRef(new Animated.Value(1)).current
    const [localVisible, setLocalVisible] = useState(visible)

    useEffect(() => {
        if (!visible) {
            // Fade out animation
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }).start(() => {
                setLocalVisible(false)
            })
        } else {
            setLocalVisible(true)
            fadeAnim.setValue(1)
        }
    }, [visible])

    if (!localVisible) return null

    return (
        <Animated.View 
            style={[
                styles.container,
                { opacity: fadeAnim }
            ]}
        >
            <LinearGradient
                colors={[theme.colors.colorPrimary, theme.colors.colorSecondary || theme.colors.colorPrimary]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.gradient}
            >
                <View style={styles.content}>
                    {/* Logo o icono de la app */}
                    <View style={styles.logoContainer}>
                        <View style={styles.logoPlaceholder}>
                            {/* Aquí podrías poner el logo de la app */}
                            <Text style={styles.logoText}>🚀</Text>
                        </View>
                    </View>

                    {/* Barra de progreso */}
                    <View style={styles.progressContainer}>
                        <View style={styles.progressBar}>
                            <View 
                                style={[
                                    styles.progressFill,
                                    { width: `${Math.min(100, Math.max(0, progress))}%` }
                                ]}
                            />
                        </View>
                        <Text style={styles.progressText}>
                            {Math.round(progress)}%
                        </Text>
                    </View>

                    {/* Mensajes */}
                    <View style={styles.messageContainer}>
                        <Text style={styles.message}>{message}</Text>
                        {subMessage && (
                            <Text style={styles.subMessage}>{subMessage}</Text>
                        )}
                    </View>

                    {/* Dots animados */}
                    <LoadingDots />
                </View>
            </LinearGradient>
        </Animated.View>
    )
}

const LoadingDots = () => {
    const dot1 = React.useRef(new Animated.Value(0)).current
    const dot2 = React.useRef(new Animated.Value(0)).current
    const dot3 = React.useRef(new Animated.Value(0)).current

    useEffect(() => {
        const animate = (dot, delay) => {
            Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(dot, {
                        toValue: 1,
                        duration: 400,
                        useNativeDriver: true,
                    }),
                    Animated.timing(dot, {
                        toValue: 0,
                        duration: 400,
                        useNativeDriver: true,
                    }),
                ])
            ).start()
        }

        animate(dot1, 0)
        animate(dot2, 150)
        animate(dot3, 300)
    }, [])

    const dotStyle = (anim) => ({
        opacity: anim,
        transform: [{
            scale: anim.interpolate({
                inputRange: [0, 1],
                outputRange: [0.8, 1.2]
            })
        }]
    })

    return (
        <View style={styles.dotsContainer}>
            <Animated.View style={[styles.dot, dotStyle(dot1)]} />
            <Animated.View style={[styles.dot, dotStyle(dot2)]} />
            <Animated.View style={[styles.dot, dotStyle(dot3)]} />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 9999,
    },
    gradient: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    content: {
        width: '100%',
        maxWidth: 400,
        alignItems: 'center',
        paddingHorizontal: 40,
    },
    logoContainer: {
        marginBottom: 48,
    },
    logoPlaceholder: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    logoText: {
        fontSize: 48,
    },
    progressContainer: {
        width: '100%',
        alignItems: 'center',
        marginBottom: 24,
    },
    progressBar: {
        width: '100%',
        height: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 3,
        overflow: 'hidden',
        marginBottom: 8,
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#FFFFFF',
        borderRadius: 3,
    },
    progressText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
    },
    messageContainer: {
        alignItems: 'center',
        marginBottom: 32,
    },
    message: {
        fontSize: 18,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 8,
        textAlign: 'center',
    },
    subMessage: {
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
    },
    dotsContainer: {
        flexDirection: 'row',
        gap: 8,
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#FFFFFF',
    },
})

export default WebInitialLoader
